<script setup lang="ts">
import { type Component } from 'vue'
import { onMounted, ref } from 'vue'
import { useCompanyDashboardStore } from '@/store/companyDashboard'
import { storeToRefs } from 'pinia'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from '@/composable/useI18n'
import { useSuccessStore } from '@/store/success'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import CreateDashboardModal from '@/components/Dashboard/Modals/CreateDashboardModal.vue'
import ConfirmationModal from '@/components/Globals/ConfirmationModal.vue'
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import { useErrorStore } from '@/store/error'
import { DASHBOARD_TYPE_VALUES, DashboardType } from '@/interfaces/enums/dashboardType'
import type { ICompanyDashboard } from '@/interfaces/entities/companyDashboard'
import {
  Squares2X2Icon,
  PlusIcon,
  ChartBarIcon,
  BoltIcon,
  WrenchScrewdriverIcon,
  BriefcaseIcon,
  SignalIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  UserIcon,
  TrashIcon,
  PencilSquareIcon,
} from '@heroicons/vue/24/outline'

const DASHBOARD_TYPE_ICONS: Record<DashboardType, Component> = {
  [DashboardType.Operational]: BoltIcon,
  [DashboardType.Maintenance]: WrenchScrewdriverIcon,
  [DashboardType.Management]: BriefcaseIcon,
  [DashboardType.Sensor]: SignalIcon,
  [DashboardType.Strategic]: ArrowTrendingUpIcon,
}

const { t } = useI18n()

const router = useRouter()
const route = useRoute()

const store = useCompanyDashboardStore()
const { dashboards } = storeToRefs(store)

const isDashboardModalOpen = ref(false)
const selectedDashboardForEdit = ref<ICompanyDashboard | null>(null)
const isLoadingDashboards = ref(true)
const isDeleteModalOpen = ref(false)
const isDeleting = ref(false)
const selectedDashboardForDelete = ref<{ id: number; name: string } | null>(null)

onMounted(async () => {
  try {
    await store.loadDashboards()
  } finally {
    isLoadingDashboards.value = false
  }
})

const navigateToDashboard = (id: number) => {
  router.push({
    name: 'customDashboards',
    params: {
      companyId: route.params.companyId,
      id
    }
  })
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getDashboardTypeLabel = (type: string) => {
  if (DASHBOARD_TYPE_VALUES.includes(type as DashboardType)) {
    return t(`custom_dashboards.types.${type as DashboardType}`)
  }
  return type
}

const getDashboardTypeIcon = (type: string): Component => {
  if (DASHBOARD_TYPE_VALUES.includes(type as DashboardType)) {
    return DASHBOARD_TYPE_ICONS[type as DashboardType]
  }
  return ChartBarIcon
}

function openCreateModal() {
  selectedDashboardForEdit.value = null
  isDashboardModalOpen.value = true
}

function openEditModal(dashboard: ICompanyDashboard) {
  selectedDashboardForEdit.value = dashboard
  isDashboardModalOpen.value = true
}

function closeDashboardModal() {
  isDashboardModalOpen.value = false
  selectedDashboardForEdit.value = null
}

async function onDashboardSaved() {
  const wasEditing = selectedDashboardForEdit.value != null
  await store.loadDashboards()
  useSuccessStore().setSuccess([
    wasEditing
      ? t('custom_dashboards.edit_modal.success')
      : t('custom_dashboards.create_modal.success'),
  ])
}

function openDeleteModal(dashboard: { id: number; name: string }) {
  selectedDashboardForDelete.value = dashboard
  isDeleteModalOpen.value = true
}

function closeDeleteModal() {
  isDeleteModalOpen.value = false
  selectedDashboardForDelete.value = null
}

async function deleteDashboard() {
  if (!selectedDashboardForDelete.value || isDeleting.value) return

  isDeleting.value = true
  try {
    await store.deleteDashboard(selectedDashboardForDelete.value.id)
    useSuccessStore().setSuccess([t('custom_dashboards.delete_modal.success')])
    closeDeleteModal()
  } catch (error: unknown) {
    const apiErrors = (error as { response?: { data?: { errors?: string | string[] } } })?.response?.data?.errors
    useErrorStore().setErrors(
      apiErrors ?? [t('custom_dashboards.delete_modal.errors.generic')],
    )
  } finally {
    isDeleting.value = false
  }
}
</script>

<template>
  <DefaultLayout>
    <div class="flex flex-col gap-8 p-6 lg:p-10 bg-gray-2 min-h-[calc(100vh-80px)]">
      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-3">
          <Squares2X2Icon class="h-8 w-8 text-primary" />
          <h1 class="font-satoshi text-3xl font-bold text-black">{{ t('custom_dashboards.catalog.title') }}</h1>
        </div>
        <p class="text-body text-lg">{{ t('custom_dashboards.catalog.subtitle') }}</p>
      </div>

      <div
        v-if="isLoadingDashboards"
        class="flex flex-col items-center justify-center gap-3 py-20"
      >
        <Loader />
        <p class="text-sm font-medium text-body">
          {{ t('custom_dashboards.catalog.loading') }}
        </p>
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <button
          type="button"
          class="group flex flex-col items-center justify-center gap-4 border-2 border-dashed border-stroke bg-white p-10 transition-all hover:border-primary hover:bg-primary/5"
          @click="openCreateModal"
        >
          <div class="flex h-16 w-16 items-center justify-center bg-gray-2 group-hover:bg-primary/10 transition-all">
            <PlusIcon class="h-8 w-8 text-body group-hover:text-primary" />
          </div>
          <div class="text-center">
            <span class="block font-satoshi text-lg font-bold text-black group-hover:text-primary">
              {{ t('custom_dashboards.catalog.create_card.title') }}
            </span>
            <span class="text-sm text-body">{{ t('custom_dashboards.catalog.create_card.subtitle') }}</span>
          </div>
        </button>

        <article
          v-for="dashboard in dashboards"
          :key="dashboard.id"
          @click="navigateToDashboard(dashboard.id)"
          class="group relative flex flex-col bg-white border border-stroke shadow-sm transition-all hover:shadow-md cursor-pointer"
        >
          <div class="h-1.5 w-full bg-primary/20 group-hover:bg-primary transition-all"></div>

          <div class="flex flex-col flex-1 p-6 transition-all group-hover:blur-[1px]">
            <div class="mb-4 flex items-start justify-between">
              <div class="flex h-12 w-12 items-center justify-center bg-gray-2 text-primary group-hover:bg-primary group-hover:text-white transition-all">
                <component :is="getDashboardTypeIcon(dashboard.dashboard_type)" class="h-6 w-6" />
              </div>
              <span class="bg-gray-2 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-body">
                {{ getDashboardTypeLabel(dashboard.dashboard_type) }}
              </span>
            </div>

            <h3 class="font-satoshi text-xl font-bold text-black mb-2 group-hover:text-primary transition-all">
              {{ dashboard.name }}
            </h3>
            <p class="text-sm text-body line-clamp-2 mb-6 flex-1">
              {{ dashboard.description }}
            </p>

            <div class="mt-auto pt-6 border-t border-stroke flex flex-col gap-3">
              <div class="flex items-center gap-2 text-xs text-body">
                <ClockIcon class="h-4 w-4" />
                <span>
                  {{ t('custom_dashboards.catalog.modified_at', {
                    date: dashboard.updated_at ? formatDate(dashboard.updated_at) : t('custom_dashboards.catalog.not_available')
                  }) }}
                </span>
              </div>
              <div class="flex items-center gap-2 text-xs text-body">
                <UserIcon class="h-4 w-4" />
                <span>
                  {{ t('custom_dashboards.catalog.created_by', {
                    name: dashboard.created_by?.name || t('custom_dashboards.catalog.not_available')
                  }) }}
                </span>
              </div>
            </div>
          </div>

          <div
            class="absolute right-4 top-4 z-20 flex gap-2 opacity-0 transition-all group-hover:opacity-100"
          >
            <button
              type="button"
              class="flex h-10 w-10 items-center justify-center bg-white text-primary shadow-md transition-all hover:bg-primary hover:text-white"
              :title="t('custom_dashboards.catalog.edit')"
              :aria-label="t('custom_dashboards.catalog.edit')"
              @click.stop="openEditModal(dashboard)"
            >
              <PencilSquareIcon class="h-5 w-5" />
            </button>
            <button
              type="button"
              class="flex h-10 w-10 items-center justify-center bg-white text-red shadow-md transition-all hover:bg-red hover:text-white"
              :title="t('custom_dashboards.catalog.delete')"
              :aria-label="t('custom_dashboards.catalog.delete')"
              @click.stop="openDeleteModal({ id: dashboard.id, name: dashboard.name })"
            >
              <TrashIcon class="h-5 w-5" />
            </button>
          </div>

          <div class="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-primary/0 opacity-0 transition-all group-hover:bg-primary/5 group-hover:opacity-100">
            <span class="bg-primary px-6 py-2 text-sm font-bold text-white shadow-lg">
              {{ t('custom_dashboards.catalog.open') }}
            </span>
          </div>
        </article>
      </div>
    </div>

    <CreateDashboardModal
      :is-open="isDashboardModalOpen"
      :dashboard="selectedDashboardForEdit"
      @close="closeDashboardModal"
      @saved="onDashboardSaved"
    />

    <ConfirmationModal
      :is-open="isDeleteModalOpen"
      :cancel-text="t('globals.buttons.cancel')"
      :confirmation-text="t('globals.buttons.remove')"
      @cancel="closeDeleteModal"
      @add="deleteDashboard"
    >
      <template #content>
        <div class="text-center">
          <h3 class="text-lg font-medium text-black-2">
            {{ t('custom_dashboards.delete_modal.title') }}
          </h3>
          <p class="mt-2 text-sm text-slate-500">
            {{ t('custom_dashboards.delete_modal.message', { name: selectedDashboardForDelete?.name ?? '' }) }}
          </p>
        </div>
      </template>
    </ConfirmationModal>
  </DefaultLayout>
</template>

<style scoped>
.font-satoshi {
  font-family: 'Satoshi', sans-serif;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
